// ===== Ударение из IPA =====
const IPA_VOWELS = new Set('aɑæʌɒɔəɛɜɪiɵʊuːeɝoʉ');
const EN_VOWELS = new Set('aeiouyAEIOUY');
const RU_VOWELS = new Set('аеёиоуыэюяАЕЁИОУЫЭЮЯ');

/**
 * Находит индекс ударной гласной в английском слове по IPA транскрипции
 */
export function getStressIndex(word: string, ipa: string): number {
  if (!ipa || !word) return -1;
  const stressPos = ipa.indexOf('ˈ') !== -1 ? ipa.indexOf('ˈ') : ipa.indexOf("'");
  if (stressPos === -1) {
    for (let i = 0; i < word.length; i++) {
      if (EN_VOWELS.has(word[i])) return i;
    }
    return -1;
  }
  let ipaVowelIdx = 0;
  for (let i = 0; i < stressPos; i++) {
    if (IPA_VOWELS.has(ipa[i])) ipaVowelIdx++;
  }
  let count = 0;
  for (let i = 0; i < word.length; i++) {
    if (EN_VOWELS.has(word[i])) {
      if (count === ipaVowelIdx) return i;
      count++;
    }
  }
  for (let i = 0; i < word.length; i++) {
    if (EN_VOWELS.has(word[i])) return i;
  }
  return -1;
}

/**
 * Вставляет знак ударения (акцент) в английское слово/фразу на основе IPA
 */
export function addStressMark(word: string, ipa: string): string {
  if (!word || !ipa) return word;
  const words = word.split(/(\s+|-)/);
  const ipaParts = ipa.replace(/[.]/g, '').split(/\s+/);
  let ipaIdx = 0;
  return words.map(part => {
    if (/^[\s-]+$/.test(part)) return part;
    const partIpa = ipaParts[ipaIdx] || '';
    ipaIdx++;
    const idx = getStressIndex(part, partIpa);
    if (idx < 0) return part;
    return part.slice(0, idx + 1) + '\u0301' + part.slice(idx + 1);
  }).join('');
}

/**
 * Вставляет знак ударения в русскую фонетику на основе IPA транскрипции.
 * Корректно расставляет ударения на русские гласные, включая дифтонги и фразы.
 */
export function addPhoneticStress(phonetic: string, ipa: string): string {
  if (!phonetic || !ipa) return phonetic;
  if (phonetic.includes('\u0301')) return phonetic;
  const cleanPh = phonetic.replace(/\u0301/g, '');

  const addStressToSingle = (ph: string, partIpa: string): string => {
    if (!ph) return ph;
    let pos = partIpa.indexOf('ˈ');
    if (pos === -1) pos = partIpa.indexOf("'");
    let targetVowelIdx = 0;
    if (pos !== -1) {
      for (let i = 0; i < pos; i++) {
        if (IPA_VOWELS.has(partIpa[i])) targetVowelIdx++;
      }
    }

    const vowelSlots: number[] = [];
    let i = 0;
    while (i < ph.length) {
      const ch = ph[i].toLowerCase();
      if (RU_VOWELS.has(ch)) {
        if (ch === 'у' && i + 1 < ph.length && RU_VOWELS.has(ph[i + 1].toLowerCase())) {
          vowelSlots.push(i + 1);
          i += 2;
          continue;
        } else if ('аоэ'.includes(ch) && i + 1 < ph.length && ph[i + 1].toLowerCase() === 'у') {
          vowelSlots.push(i);
          i += 2;
          continue;
        } else {
          vowelSlots.push(i);
          i += 1;
        }
      } else {
        i += 1;
      }
    }

    if (vowelSlots.length === 0) return ph;
    const stressIdx = targetVowelIdx < vowelSlots.length ? vowelSlots[targetVowelIdx] : vowelSlots[0];
    return ph.slice(0, stressIdx + 1) + '\u0301' + ph.slice(stressIdx + 1);
  };

  const phParts = cleanPh.split(/(\s+)/);
  const ipaParts = ipa.replace(/[.]/g, '').split(/\s+/).filter(Boolean);
  let ipaIdx = 0;
  return phParts.map(part => {
    if (/^\s+$/.test(part)) return part;
    const partIpa = ipaParts[ipaIdx] || '';
    ipaIdx++;
    return addStressToSingle(part, partIpa);
  }).join('');
}
